import {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDebouncedCallback} from 'use-debounce';
import {useDispatch, useSelector} from 'react-redux';
import {MOBILE_WORKSPACE_SIZE_DEFAULT, WORKSPACE_SIZE_DEFAULT} from '../../constants';
import {selectedPresentationSelector, selectedViewSelector} from '../../store/selectors/presentation.selectors';
import {selectedSlideSelector} from '../../store/selectors/slide.selectors';
import {stopWindowResizeSpinnerAction} from '../../store/actions/spinner';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {SlideRefContext} from '../../context/SlideRefContext';
import {windowResizeSpinnerSelector} from '../../store/selectors/spinner.selector';

function usePresScale(): {
    scale: number;
    slideSize: {
        width: number;
        height: number;
    };
    workspaceSize: {
        width: number;
        height: number;
    };
    handleResizeDebounced: () => void;
    slideRect: DOMRect | null;
    transformPointFromFraction: (point: {x: number; y: number}) => {x: number; y: number};
    transformPointToFraction: (point: {x: number; y: number}) => {x: number; y: number};
    transformWidthFromPercentage: (length: string) => number;
    transformHeightFromPercentage: (length: string) => number;
    transformWidthToPercentage: (length: number) => string;
    transformHeightToPercentage: (length: number) => string;
} {
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const slide = useSelector(selectedSlideSelector);
    const dispatch = useDispatch();
    const selectedView = useSelector(selectedViewSelector);
    const windowResizeSpinner = useSelector(windowResizeSpinnerSelector);
    const {slideRef, slidesContainerWrapperRef} = useContext(SlideRefContext);

    // TODO: This seems to work with only slidesContainerWrapperRef; needs checking if slideRef is necessary
    const container = selectedView === DeviceType.MOBILE ? slideRef : slidesContainerWrapperRef;
    const baseContainerSize = selectedView === DeviceType.MOBILE ? MOBILE_WORKSPACE_SIZE_DEFAULT : WORKSPACE_SIZE_DEFAULT;

    const [slideSize, setSlideSize] = useState({width: 0, height: 0});
    const [slideRect, setSlideRect] = useState<DOMRect | null>(null);

    useEffect(() => {
        const handleSlideResize = (): void => {
            if (windowResizeSpinner) return;
            const rect = slideRef.current?.getBoundingClientRect() || null;
            setSlideRect(rect);
        };

        handleSlideResize();

        window.addEventListener('resize', handleSlideResize);

        return () => {
            window.removeEventListener('resize', handleSlideResize);
        };
    }, [slideRef, windowResizeSpinner]);

    const getWorkspaceSize = useCallback(() => {
        if (!slide || !selectedPresentation) return baseContainerSize;

        const currentSlideSize = slide?.workspaceSize;
        const loadedPresentationSize = selectedPresentation.workspaceSize;

        const width = currentSlideSize?.width || loadedPresentationSize?.width || baseContainerSize.width;
        const height = currentSlideSize?.height || loadedPresentationSize?.height || baseContainerSize.height;

        return {width, height};
    }, [selectedPresentation, slide, baseContainerSize]);

    // slide width / height ratio
    const getSlideSizeRatio = useCallback((): number | null => {
        if (!slide || !selectedPresentation) return null;
        const {width, height} = getWorkspaceSize();
        return width / height;
    }, [getWorkspaceSize, selectedPresentation, slide]);

    const measureCanvas = useCallback((): boolean => {
        const slideSizeRatio = getSlideSizeRatio();
        if (!slideSizeRatio) return false;

        const slideContainer = container.current;
        if (!slideContainer) return false;

        const {width, height} = slideContainer.getBoundingClientRect();
        let newWidth = width;
        let newHeight = height;

        if (slideSizeRatio > 1) {
            newHeight = width / slideSizeRatio;

            if (newHeight > height) {
                newHeight = height;
                newWidth = height * slideSizeRatio;
            }
        } else if (slideSizeRatio < 1) {
            newWidth = height * slideSizeRatio;

            if (newWidth > width) {
                newWidth = width;
                newHeight = width / slideSizeRatio;
            }
        }

        setSlideSize((prev) =>
            Math.abs(prev.width - newWidth) < 0.5 && Math.abs(prev.height - newHeight) < 0.5 ? prev : {width: newWidth, height: newHeight}
        );
        setSlideRect(slideRef.current?.getBoundingClientRect() ?? null);
        return true;
    }, [container, getSlideSizeRatio, slideRef]);

    const measureCanvasRef = useRef(measureCanvas);
    measureCanvasRef.current = measureCanvas;

    const handleResize = useCallback((): void => {
        if (measureCanvasRef.current()) {
            dispatch(stopWindowResizeSpinnerAction());
        }
    }, [dispatch]);

    useLayoutEffect((): void | (() => void) => {
        setSlideSize({width: 0, height: 0});

        if (windowResizeSpinner) return undefined;

        const rafId = requestAnimationFrame(() => {
            measureCanvasRef.current();
        });
        return () => cancelAnimationFrame(rafId);
    }, [selectedView, windowResizeSpinner]);

    const handleResizeDebounced = useDebouncedCallback(() => {
        if (windowResizeSpinner) return;
        handleResize();
    }, 100);

    const workspaceSize = getWorkspaceSize();

    const transformPointFromFraction = useCallback(
        (point: {x: number; y: number}): {x: number; y: number} => {
            const {width, height} = getWorkspaceSize();
            if (!width || !height) return point;
            return {
                x: point.x * width,
                y: point.y * height,
            };
        },
        [getWorkspaceSize]
    );

    const transformPointToFraction = useCallback(
        (point: {x: number; y: number}): {x: number; y: number} => {
            const {width, height} = getWorkspaceSize();
            if (!width || !height) return point;
            return {
                x: point.x / width,
                y: point.y / height,
            };
        },
        [getWorkspaceSize]
    );

    const transformWidthFromPercentage = useCallback(
        (width: string): number => {
            const {width: wsWidth} = getWorkspaceSize();
            if (!wsWidth) return 0;
            const widthNumber = parseFloat(width.replace('%', ''));
            return (widthNumber / 100) * wsWidth;
        },
        [getWorkspaceSize]
    );

    const transformHeightFromPercentage = useCallback(
        (height: string): number => {
            const {height: wsHeight} = getWorkspaceSize();
            if (!wsHeight) return 0;
            const heightNumber = parseFloat(height.replace('%', ''));
            return (heightNumber / 100) * wsHeight;
        },
        [getWorkspaceSize]
    );

    const transformWidthToPercentage = useCallback(
        (width: number): string => {
            const {width: wsWidth} = getWorkspaceSize();
            if (!wsWidth) return '0%';
            const widthPercentage = (width / wsWidth) * 100;
            return `${widthPercentage}%`;
        },
        [getWorkspaceSize]
    );

    const transformHeightToPercentage = useCallback(
        (height: number): string => {
            const {height: wsHeight} = getWorkspaceSize();
            if (!wsHeight) return '0%';
            const heightPercentage = (height / wsHeight) * 100;
            return `${heightPercentage}%`;
        },
        [getWorkspaceSize]
    );

    return {
        scale: slideSize.width / workspaceSize.width,
        slideSize,
        workspaceSize,
        handleResizeDebounced,
        slideRect,
        transformPointFromFraction,
        transformPointToFraction,
        transformWidthFromPercentage,
        transformHeightFromPercentage,
        transformWidthToPercentage,
        transformHeightToPercentage,
    };
}

export default usePresScale;
